import { motion } from 'framer-motion'

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
}

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
}

const chipStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05 } },
}

const chipItem = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
}

const tools = [
  { name: 'Robot Framework', category: 'test' },
  { name: 'Cypress', category: 'test' },
  { name: 'Playwright', category: 'test' },
  { name: 'Python', category: 'dev' },
  { name: 'TypeScript', category: 'dev' },
  { name: 'AWS CDK', category: 'cloud' },
  { name: 'AWS IoT', category: 'cloud' },
  { name: 'GitHub Actions', category: 'cicd' },
  { name: 'Jenkins', category: 'cicd' },
  { name: 'Docker', category: 'cicd' },
  { name: 'Embedded / IoT', category: 'test' },
  { name: 'REST / GraphQL', category: 'dev' },
]

const categoryStyle: Record<string, string> = {
  test: 'bg-blue-50 dark:bg-blue-900/25 text-blue-700 dark:text-blue-300 border-blue-100 dark:border-blue-800/60',
  dev:  'bg-violet-50 dark:bg-violet-900/25 text-violet-700 dark:text-violet-300 border-violet-100 dark:border-violet-800/60',
  cloud: 'bg-amber-50 dark:bg-amber-900/25 text-amber-700 dark:text-amber-300 border-amber-100 dark:border-amber-800/60',
  cicd: 'bg-emerald-50 dark:bg-emerald-900/25 text-emerald-700 dark:text-emerald-300 border-emerald-100 dark:border-emerald-800/60',
}

const legend = [
  { category: 'test', label: 'Testaus' },
  { category: 'dev', label: 'Kehitys' },
  { category: 'cloud', label: 'Pilvi' },
  { category: 'cicd', label: 'CI/CD' },
]

const certs = [
  { name: 'Cloud Practitioner', abbr: 'CLF', color: 'from-emerald-500 to-teal-600' },
  { name: 'Solutions Architect', abbr: 'SAA', color: 'from-blue-500 to-blue-700' },
  { name: 'SysOps Administrator', abbr: 'SOA', color: 'from-violet-500 to-purple-700' },
  { name: 'DevOps Engineer\nProfessional', abbr: 'DOP', color: 'from-orange-500 to-red-600' },
]

export default function Expertise() {
  return (
    <section id="osaaminen" className="py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
            Osaaminen
          </span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white">
            Teknologiat & sertifikaatit
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Tools */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.h3
              variants={fadeUp}
              className="text-lg font-bold text-gray-900 dark:text-white mb-3"
            >
              Työkalut & teknologiat
            </motion.h3>

            {/* Legend */}
            <motion.div variants={fadeUp} className="flex flex-wrap gap-2 mb-6">
              {legend.map(({ category, label }) => (
                <span
                  key={category}
                  className={`text-xs font-medium px-2.5 py-1 rounded-full border ${categoryStyle[category]}`}
                >
                  {label}
                </span>
              ))}
            </motion.div>

            {/* Tool chips */}
            <motion.div
              variants={chipStagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="flex flex-wrap gap-2.5"
            >
              {tools.map(({ name, category }) => (
                <motion.span
                  key={name}
                  variants={chipItem}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-sm font-medium cursor-default select-none transition-transform duration-200 hover:scale-105 ${categoryStyle[category]}`}
                >
                  {name}
                </motion.span>
              ))}
            </motion.div>
          </motion.div>

          {/* Certifications */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.h3
              variants={fadeUp}
              className="text-lg font-bold text-gray-900 dark:text-white mb-8"
            >
              AWS-sertifikaatit
            </motion.h3>

            <motion.div variants={fadeUp} className="grid grid-cols-2 gap-4">
              {certs.map(({ name, abbr, color }) => (
                <div
                  key={abbr}
                  className="flex flex-col items-center justify-center p-6 rounded-2xl bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800 text-center group hover:border-blue-200 dark:hover:border-blue-800 transition-colors"
                >
                  <div
                    className={`w-14 h-14 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white font-bold text-sm mb-3 group-hover:scale-110 transition-transform duration-300`}
                  >
                    {abbr}
                  </div>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300 leading-snug whitespace-pre-line">
                    AWS {name}
                  </span>
                </div>
              ))}
            </motion.div>

            <motion.div
              variants={fadeUp}
              className="mt-8 p-6 rounded-2xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50"
            >
              <div className="flex items-center gap-4">
                <div className="text-4xl font-extrabold text-blue-600 dark:text-blue-400">20+</div>
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    Vuotta kokemusta
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500">
                    Verkkoinfrastruktuurista pilvi-IoT:hin
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
